import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, PencilSimple, Trash, PaintBrushBroad, Eye } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { useRole } from "@/hooks/useRole";
import CanvasEditor from "@/components/studio/CanvasEditor";
import { DEFAULT_BRAND, PRESETS, buildPreset, PLATFORM_SIZES } from "@/components/studio/presets";
import { FullPreviewModal } from "@/components/FullPreview/FullPreviewModal";

export default function Studio() {
  const api = useApi();
  const { canManageContent } = useRole();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [editing, setEditing] = useState(null); // {id?, ...design} or 'new'
  const [deleteId, setDeleteId] = useState(null);
  const [fullPreview, setFullPreview] = useState(null);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    try {
      const [dRes, wRes] = await Promise.all([api.get("/designs"), api.get("/workspace")]);
      setDesigns(dRes.data);
      const bcfg = wRes.data?.settings?.brand;
      if (bcfg) setBrand({ ...DEFAULT_BRAND, ...bcfg });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const startNew = (presetKey) => {
    setEditing({
      name: "Untitled composition",
      platform: "1:1",
      canvas_json: buildPreset(presetKey, brand, PLATFORM_SIZES["1:1"]),
    });
  };

  const openDesign = async (d) => {
    try {
      const res = await api.get(`/designs/${d.id}`);
      setEditing(res.data);
    } catch { toast.error("Could not open design"); }
  };

  const removeDesign = async (id) => {
    try {
      await api.delete(`/designs/${id}`);
      toast.success("Design deleted");
      fetchAll();
    } catch { toast.error("Delete failed"); }
    finally { setDeleteId(null); }
  };

  if (!canManageContent) {
    return <div className="text-[#7A9090] p-8" data-testid="studio-no-access">You don't have access to the Design Studio.</div>;
  }

  if (editing) {
    return (
      <CanvasEditor
        api={api}
        brand={brand}
        initialDesign={editing.id ? editing : { ...editing }}
        onClose={() => { setEditing(null); }}
        onSaved={fetchAll}
      />
    );
  }

  return (
    <div className="space-y-8" data-testid="studio-container">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold text-[#3C4048] mb-2" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Design Studio</h1>
          <p className="text-[#7A9090]">Compose on-brand, corporate-quality social posts. Light, clean, teal — every time.</p>
        </div>
      </div>

      {/* Preset starters */}
      <div>
        <p className="text-[#7A9090] text-xs font-semibold uppercase tracking-widest mb-3">Start from a preset</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PRESETS.map((p) => (
            <button key={p.key} data-testid={`start-preset-${p.key}`} onClick={() => startNew(p.key)}
              className="group text-left rounded-[12px] border border-[#e5e7eb] bg-white p-5 hover:border-[#5FA8B4] hover:shadow-sm transition-all">
              <div className="w-10 h-10 rounded-[10px] bg-[#f0f7f8] flex items-center justify-center mb-3 group-hover:bg-[#5FA8B4] transition-colors">
                <PaintBrushBroad size={20} className="text-[#3A7A8C] group-hover:text-white transition-colors" />
              </div>
              <p className="text-sm font-bold text-[#3C4048]">{p.name}</p>
              <p className="text-xs text-[#7A9090] mt-1 leading-snug">{p.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Saved designs */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[#7A9090] text-xs font-semibold uppercase tracking-widest">Saved designs</p>
          <Button data-testid="new-blank-design" onClick={() => startNew("clean_professional")} className="bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white gap-2 rounded-[12px]">
            <Plus size={16} weight="bold" /> New composition
          </Button>
        </div>
        {loading ? (
          <p className="text-[#7A9090]">Loading…</p>
        ) : designs.length === 0 ? (
          <div className="rounded-[12px] border border-dashed border-[#d5dbdc] bg-[#fafafa] p-12 text-center">
            <p className="text-[#7A9090]">No saved designs yet. Pick a preset above to begin.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {designs.map((d) => (
              <div key={d.id} data-testid={`design-card-${d.id}`} className="rounded-[12px] border border-[#e5e7eb] bg-white overflow-hidden group">
                <div className="aspect-square bg-[#f5f5f5] flex items-center justify-center overflow-hidden">
                  {d.thumbnail_url ? (
                    <img src={d.thumbnail_url} alt={d.name} className="w-full h-full object-contain" />
                  ) : (
                    <PaintBrushBroad size={28} className="text-[#c7d0d2]" />
                  )}
                </div>
                <div className="p-3">
                  <p className="text-sm font-semibold text-[#3C4048] truncate">{d.name}</p>
                  <p className="text-xs text-[#7A9090] mt-0.5">{d.platform} · {d.preset || "custom"}</p>
                  <div className="flex gap-2 mt-3">
                    {d.thumbnail_url && (
                      <Button size="sm" variant="ghost" onClick={() => setFullPreview({ type: "image", data: { mediaUrl: d.thumbnail_url, filename: d.name } })}
                        className="text-[#3A7A8C] hover:bg-[#f0f7f8] h-8 px-2" data-testid={`full-preview-design-${d.id}`}>
                        <Eye size={13} />
                      </Button>
                    )}
                    <Button size="sm" onClick={() => openDesign(d)} className="flex-1 bg-[#f0f7f8] text-[#3A7A8C] hover:bg-[#5FA8B4] hover:text-white gap-1 rounded-[8px] h-8" data-testid={`edit-design-${d.id}`}>
                      <PencilSimple size={13} /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleteId(d.id)} className="text-[#ef4444] hover:bg-[#fef2f2] h-8 px-2" data-testid={`delete-design-${d.id}`}>
                      <Trash size={13} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-white border-[#e5e7eb]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Delete this design?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#7A9090]">This permanently removes the saved design. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#e5e7eb] text-[#3C4048]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-design-confirm" onClick={() => removeDesign(deleteId)} className="bg-[#ef4444] text-white hover:bg-[#dc2626]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <FullPreviewModal open={!!fullPreview} onOpenChange={(v) => !v && setFullPreview(null)} preview={fullPreview} />
    </div>
  );
}
